import { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Select,
  HStack,
  Input,
  Button,
  useToast,
  Spinner,
  Badge,
  Tooltip,
} from '@chakra-ui/react';

interface LogEntry {
  id: number;
  user_email: string;
  user_name: string;
  action: string;
  resource: string;
  timestamp: string;
}

export function AccessLogs() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [logsPerPage] = useState(15);
  const toast = useToast();

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/access-logs', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      } else {
        throw new Error('Failed to fetch logs');
      }
    } catch (error) {
      toast({
        title: 'Error fetching logs',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filteredLogs = logs.filter(log => {
    // Always exclude "confirmed_matches" entries
    if (log.resource === "confirmed_matches") return false;

    const matchesSearch = 
      log.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.resource.toLowerCase().includes(searchTerm.toLowerCase());

    if (filter === 'all') return matchesSearch;
    
    // Updated filtering logic based on actual resource values
    switch(filter) {
      case 'login':
        return log.resource === 'auth' && log.action.toLowerCase().includes('login') && matchesSearch;
      case 'document_upload':
        return log.resource === 'document_upload' && matchesSearch;
      case 'confirm_match':
        return log.resource === 'insight_confirm_match' && matchesSearch;
      case 'reject_match':
        return log.resource === 'insight_reject_match' && matchesSearch;
      case 'search':
        return (log.resource === 'insight_search' || log.resource === 'rtc_ai_search') && matchesSearch;
      case 'stream':
        return log.resource === 'stream' && matchesSearch;
      case 'guacamole':
        return log.resource === 'Guacamole' && matchesSearch;
      case 'report':
        return log.resource === 'insight_report' && matchesSearch;
      default:
        return matchesSearch;
    }
  });

  // Get current logs for pagination
  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = filteredLogs.slice(indexOfFirstLog, indexOfLastLog);
  const totalPages = Math.ceil(filteredLogs.length / logsPerPage);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Reset to first page when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatLogAction = (action: string) => {
    if (action.includes('Searched with file')) {
      const matchesMatch = action.match(/Found (\d+) matches/);
      return `Searched an image${matchesMatch ? `. Found ${matchesMatch[1]} matches` : ''}`;
    }
    if (action.includes('Searched with query:')) {
      const queryMatch = action.match(/query: "([^"]+)"/);
      const contextMatch = action.match(/context: ({[^}]+})/);
      
      if (queryMatch && contextMatch) {
        const query = queryMatch[1];
        const context = JSON.parse(contextMatch[1]);
        
        // Check if it's the default query
        const isDefaultQuery = query === "Please describe the bulletins that match the listed context";
        
        // Extract non-empty context fields
        const contextStr = Object.entries(context)
          .filter(([key, value]) => value && value !== '')
          .map(([key, value]) => {
            // Format the key to be more readable
            const formattedKey = key.split('_').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ');
            
            // Special handling for date ranges
            if (key === 'time_from' || key === 'time_to') {
              return null;
            }
            
            return `${formattedKey}: ${value}`;
          })
          .filter(Boolean) // Remove null values
          .join('; ');
        
        if (isDefaultQuery) {
          return contextStr ? `Searched with default query. Context: ${contextStr}` : 'Searched with default query';
        } else {
          return `Search: ${query.substring(0, 50)}${query.length > 50 ? '...' : ''}${contextStr ? ` [${contextStr}]` : ''}`;
        }
      }
    }
    return action;
  };

  const getActionColor = (action: string, resource: string): string => {
    const actionLower = action.toLowerCase();
    const resourceLower = resource.toLowerCase();
    
    if (resourceLower === 'rtc_ai_search') return 'cyan';
    if (resourceLower === 'insight_search') return 'blue';
    if (resourceLower === 'document_upload') return 'purple';
    if (resourceLower === 'auth' && actionLower.includes('login')) return 'green';
    if (resourceLower === 'insight_confirm_match') return 'teal';
    if (resourceLower === 'insight_reject_match') return 'red';
    if (resourceLower === 'stream') return 'orange';
    if (resourceLower === 'guacamole') return 'yellow';
    if (resourceLower === 'insight_report') return 'pink';
    return 'gray';
  };

  return (
    <Box 
      width="100%"
      p={[4, 6, 8]}
    >
      <Text fontSize="2xl" fontWeight="bold" mb={6}>Access Logs</Text>
      
      <HStack spacing={4} mb={6}>
        <Select 
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          width="200px"
          bg="white"
          borderRadius="md"
        >
          <option value="all">All Resources</option>
          <option value="login">Login</option>
          <option value="document_upload">Document Upload</option>
          <option value="confirm_match">Confirmed Matches</option>
          <option value="reject_match">Rejected Matches</option>
          <option value="search">Search</option>
          <option value="rtc_ai_search">RTC AI Search</option>
          <option value="stream">Video Streams</option>
          <option value="guacamole">Remote Desktop</option>
          <option value="report">Generated Reports</option>
        </Select>
        <Input
          placeholder="Search logs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          flex="1"
          bg="white"
          borderRadius="md"
        />
        <Button 
          colorScheme="blue"
          onClick={fetchLogs}
          borderRadius="md"
        >
          Refresh
        </Button>
      </HStack>

      {loading ? (
        <Box textAlign="center" py={8}>
          <Spinner size="xl" />
          <Text mt={4}>Loading logs...</Text>
        </Box>
      ) : (
        <>
          <Box overflowX="auto">
            <Table variant="simple" width="100%" size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th py={2}>Time</Th>
                  <Th py={2}>User</Th>
                  <Th py={2}>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {currentLogs.map((log) => (
                  <Tr key={log.id} _hover={{ bg: 'gray.50' }}>
                    <Td py={2} fontSize="sm">{formatTimestamp(log.timestamp)}</Td>
                    <Td py={2}>
                      <Text fontSize="sm" fontWeight="medium">{log.user_name}</Text>
                      <Text fontSize="xs" color="gray.500">{log.user_email}</Text>
                    </Td>
                    <Td py={2}>
                      <Tooltip label={log.action} placement="top">
                        <Badge 
                          colorScheme={getActionColor(log.action, log.resource)}
                          fontSize="xs"
                          maxW="500px"
                          whiteSpace="pre-wrap"
                          isTruncated
                          px={2}
                          py={1}
                        >
                          {formatLogAction(log.action)}
                        </Badge>
                      </Tooltip>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            
            {filteredLogs.length === 0 && (
              <Box textAlign="center" py={8}>
                <Text color="gray.500">No logs found</Text>
              </Box>
            )}
          </Box>
          
          {/* Pagination */}
          {filteredLogs.length > 0 && (
            <HStack spacing={2} mt={6} justify="center">
              <Button
                size="sm"
                onClick={() => paginate(currentPage - 1)}
                isDisabled={currentPage === 1}
              >
                Previous
              </Button>
              
              {totalPages <= 7 ? (
                // Show all page numbers if there are 7 or fewer
                Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                  <Button
                    key={number}
                    size="sm"
                    colorScheme={currentPage === number ? "blue" : "gray"}
                    onClick={() => paginate(number)}
                  >
                    {number}
                  </Button>
                ))
              ) : (
                // Show limited page numbers with ellipsis for many pages
                <>
                  {[1, 2, 3].includes(currentPage) && (
                    <>
                      {[1, 2, 3, 4, 5].map(number => (
                        <Button
                          key={number}
                          size="sm"
                          colorScheme={currentPage === number ? "blue" : "gray"}
                          onClick={() => paginate(number)}
                        >
                          {number}
                        </Button>
                      ))}
                      <Text>...</Text>
                      <Button
                        size="sm"
                        colorScheme="gray"
                        onClick={() => paginate(totalPages)}
                      >
                        {totalPages}
                      </Button>
                    </>
                  )}
                  
                  {currentPage > 3 && currentPage < totalPages - 2 && (
                    <>
                      <Button
                        size="sm"
                        colorScheme="gray"
                        onClick={() => paginate(1)}
                      >
                        1
                      </Button>
                      <Text>...</Text>
                      {[currentPage - 1, currentPage, currentPage + 1].map(number => (
                        <Button
                          key={number}
                          size="sm"
                          colorScheme={currentPage === number ? "blue" : "gray"}
                          onClick={() => paginate(number)}
                        >
                          {number}
                        </Button>
                      ))}
                      <Text>...</Text>
                      <Button
                        size="sm"
                        colorScheme="gray"
                        onClick={() => paginate(totalPages)}
                      >
                        {totalPages}
                      </Button>
                    </>
                  )}
                  
                  {[totalPages - 2, totalPages - 1, totalPages].includes(currentPage) && (
                    <>
                      <Button
                        size="sm"
                        colorScheme="gray"
                        onClick={() => paginate(1)}
                      >
                        1
                      </Button>
                      <Text>...</Text>
                      {[totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages].map(number => (
                        <Button
                          key={number}
                          size="sm"
                          colorScheme={currentPage === number ? "blue" : "gray"}
                          onClick={() => paginate(number)}
                        >
                          {number}
                        </Button>
                      ))}
                    </>
                  )}
                </>
              )}
              
              <Button
                size="sm"
                onClick={() => paginate(currentPage + 1)}
                isDisabled={currentPage === totalPages}
              >
                Next
              </Button>
            </HStack>
          )}
        </>
      )}
    </Box>
  );
}

export default AccessLogs;